import React, { useState, useEffect, useCallback, useRef } from 'react';

/**
 * BreathSplashScreen - A calming breath-mirroring splash before login
 * 
 * Phases:
 * 1. Inhale cue (0-4s): "Take a slow breath in..." + icon grows
 * 2. Exhale cue (4-9s): "Gently let it back out" + icon shrinks
 * 3. Reset + reposition (9-10s): Text fades, icon moves to top
 * 4. Welcome message (10-12s): Welcome text appears
 * 5. Login transition (12s+): Fade to login
 * 
 * Click/tap anywhere to skip instantly - no confirmation, no visible button
 */

const PHASE_TIMINGS = {
  INHALE_START: 0,
  INHALE_END: 4000,
  EXHALE_START: 4000,
  EXHALE_END: 9000,
  REPOSITION_START: 9000,
  REPOSITION_END: 10000,
  WELCOME_START: 10000,
  WELCOME_END: 14000,  // Extended to 4 seconds (was 2 seconds)
  LOGIN_START: 14000
};

export default function BreathSplashScreen({ onComplete }) {
  const [phase, setPhase] = useState('inhale'); // inhale, exhale, reposition, welcome, done
  const [textOpacity, setTextOpacity] = useState(0);
  const [iconScale, setIconScale] = useState(0.7); // Start smaller for more dramatic growth
  const [iconPosition, setIconPosition] = useState('bottom'); // bottom, top
  const [welcomeOpacity, setWelcomeOpacity] = useState(0);
  const [containerOpacity, setContainerOpacity] = useState(1);
  const [isSkipping, setIsSkipping] = useState(false);
  
  const animationFrameRef = useRef(null);
  const startTimeRef = useRef(null);
  const completedRef = useRef(false);

  // Skip handler - instant, clean cancellation
  const handleSkip = useCallback(() => {
    if (completedRef.current || isSkipping) return;
    
    setIsSkipping(true);
    completedRef.current = true;
    
    // Cancel any pending animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    // Immediately clear all visual states and transition
    setTextOpacity(0);
    setWelcomeOpacity(0);
    setContainerOpacity(0);
    
    // Short delay for fade out, then complete
    setTimeout(() => {
      onComplete();
    }, 300);
  }, [onComplete, isSkipping]);

  // Main animation loop
  useEffect(() => {
    if (isSkipping) return;
    
    startTimeRef.current = performance.now();
    
    const animate = (currentTime) => {
      if (completedRef.current) return;
      
      const elapsed = currentTime - startTimeRef.current;
      
      // Phase 1: Inhale (0-4s)
      if (elapsed < PHASE_TIMINGS.INHALE_END) {
        setPhase('inhale');
        
        // Fade in text over 800ms
        if (elapsed < 800) {
          setTextOpacity(elapsed / 800);
        } else {
          setTextOpacity(1);
        }
        
        // Icon grows from 0.7 to 1.3 over 4s (more dramatic 60% growth)
        const inhaleProgress = elapsed / PHASE_TIMINGS.INHALE_END;
        // Use ease-in-out curve for smooth breathing feel
        const easedProgress = inhaleProgress < 0.5 
          ? 2 * inhaleProgress * inhaleProgress 
          : 1 - Math.pow(-2 * inhaleProgress + 2, 2) / 2;
        setIconScale(0.7 + (0.6 * easedProgress));
      }
      // Phase 2: Exhale (4-9s)
      else if (elapsed < PHASE_TIMINGS.EXHALE_END) {
        setPhase('exhale');
        
        // Text stays visible
        setTextOpacity(1);
        
        // Icon shrinks from 1.3 to 0.7 over 5s (matching the growth range)
        const exhaleProgress = (elapsed - PHASE_TIMINGS.EXHALE_START) / (PHASE_TIMINGS.EXHALE_END - PHASE_TIMINGS.EXHALE_START);
        // Use ease-in-out curve
        const easedProgress = exhaleProgress < 0.5 
          ? 2 * exhaleProgress * exhaleProgress 
          : 1 - Math.pow(-2 * exhaleProgress + 2, 2) / 2;
        setIconScale(1.3 - (0.6 * easedProgress));
      }
      // Phase 3: Reposition (9-10s)
      else if (elapsed < PHASE_TIMINGS.REPOSITION_END) {
        setPhase('reposition');
        
        // Fade out breath text over 500ms
        const repoElapsed = elapsed - PHASE_TIMINGS.REPOSITION_START;
        if (repoElapsed < 500) {
          setTextOpacity(1 - (repoElapsed / 500));
        } else {
          setTextOpacity(0);
        }
        
        // Move icon to top
        setIconPosition('top');
        setIconScale(1.0);
      }
      // Phase 4: Welcome (10-12s)
      else if (elapsed < PHASE_TIMINGS.WELCOME_END) {
        setPhase('welcome');
        setTextOpacity(0);
        setIconPosition('top');
        
        // Fade in welcome text over 600ms
        const welcomeElapsed = elapsed - PHASE_TIMINGS.WELCOME_START;
        if (welcomeElapsed < 600) {
          setWelcomeOpacity(welcomeElapsed / 600);
        } else {
          setWelcomeOpacity(1);
        }
      }
      // Phase 5: Transition to login
      else {
        setPhase('done');
        
        // Fade out welcome over 500ms
        const doneElapsed = elapsed - PHASE_TIMINGS.WELCOME_END;
        if (doneElapsed < 500) {
          setWelcomeOpacity(1 - (doneElapsed / 500));
          setContainerOpacity(1 - (doneElapsed / 500));
        } else {
          // Complete
          completedRef.current = true;
          onComplete();
          return;
        }
      }
      
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animationFrameRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [onComplete, isSkipping]);

  // Keyboard support for accessibility
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
        handleSkip();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSkip]);

  const breathText = phase === 'inhale' 
    ? 'Take a slow breath in…'
    : phase === 'exhale' 
    ? 'Gently let it back out'
    : '';

  return (
    <div 
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center cursor-pointer select-none"
      style={{ 
        background: '#2c4240',
        opacity: containerOpacity,
        transition: 'opacity 300ms ease-out'
      }}
      onClick={handleSkip}
      onTouchStart={handleSkip}
      role="button"
      tabIndex={0}
      aria-label="Click anywhere to skip to login"
    >
      {/* Icon - animated with CSS for smooth scaling */}
      <div 
        className="absolute"
        style={{
          top: iconPosition === 'top' ? '15%' : '30%',
          left: '50%',
          transform: `translateX(-50%) scale(${iconScale})`,
          transition: 'transform 0.1s linear, top 1s ease-in-out',
          willChange: 'transform'
        }}
      >
        <div 
          className="rounded-full overflow-hidden"
          style={{ 
            width: '140px', 
            height: '140px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }}
        >
          <img 
            src="/logo192.png" 
            alt="CalmGuard" 
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'contain' 
            }} 
          />
        </div>
      </div>

      {/* Breath text - below icon, gradient purple to teal */}
      {(phase === 'inhale' || phase === 'exhale') && (
        <div 
          className="absolute text-center px-8 transition-opacity duration-500 ease-in-out"
          style={{ 
            opacity: textOpacity,
            top: '55%',
            left: '50%',
            transform: 'translateX(-50%)'
          }}
        >
          <p 
            className="text-2xl sm:text-3xl font-semibold"
            style={{ 
              fontFamily: '"Nunito", "Quicksand", system-ui, sans-serif',
              background: 'linear-gradient(135deg, #d8b4fe 0%, #6ee7b7 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '0.02em',
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
            }}
          >
            {breathText}
          </p>
        </div>
      )}

      {/* Welcome text - appears after reposition */}
      {(phase === 'welcome' || phase === 'done') && (
        <div 
          className="absolute text-center px-8 transition-opacity duration-500 ease-in-out"
          style={{ 
            opacity: welcomeOpacity,
            top: '45%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: '500px'
          }}
        >
          <h1 
            className="text-3xl sm:text-4xl font-semibold mb-3"
            style={{ 
              fontFamily: '"Nunito", "Quicksand", system-ui, sans-serif',
              background: 'linear-gradient(135deg, #d8b4fe 0%, #6ee7b7 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            Welcome back to CalmGuard
          </h1>
          <p 
            className="text-base sm:text-lg"
            style={{ 
              fontFamily: '"Nunito", "Quicksand", system-ui, sans-serif',
              color: '#e5e7eb',
              maxWidth: '400px',
              margin: '0 auto',
              lineHeight: 1.6
            }}
          >
            Your digital space for steadiness, clarity, and calm responses
          </p>
        </div>
      )}
    </div>
  );
}
